import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useNavigate, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "../../../../components/ui/form";
import { Textarea } from "../../../../components/ui/textarea";
import StarIcon from "../../../../assets/svgs/common/star.svg";
import type { ISocial } from "../../../../types/models/social.models.types";
import { useSocialRejectAdminHttp } from "../../../../hooks/http/admin/socials/social-reject.admin.http";
import useUserStore from "../../../../store/socials.store";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";

const FormSchema = z.object({
	rejection_reason: z.string().min(5, "Please provide a reason for rejection"),
});

export const RejectSocialModalPartial: React.FunctionComponent = () => {
	const { userId } = useParams();
	const { execute: rejectSocialsHttp, fetching } = useSocialRejectAdminHttp();
	const navigate = useNavigate();
	const { data, setData, removeById } = useUserStore();
	const { toast } = useToast();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			rejection_reason: "",
		},
	});

	function rejectUser(id: string, response: ISocial) {
		const updatedData = data.map((item) => (item.id === id ? response : item));

		setData([...updatedData] as ISocial[]);
		removeById(id);
		navigate("/admin/socials");
	}

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		if (!userId) return;
		const response = await rejectSocialsHttp({
			body: { message: data.rejection_reason },
			pathParams: { id: userId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}

		rejectUser(userId, response.data.social);
	}

	return (
		<DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
			<DialogHeader className="w-full">
				<div className="flex flex-col">
					<p className="flex gap-1 font-semibold text-28 leading-8">
						Reason for rejecting user request <StarIcon />
					</p>
					<span className="text-secondary">
						Give a brief for the rejection of the user request
					</span>
				</div>
			</DialogHeader>
			<div className="border-b border-border w-full" />
			<div className="flex items-center gap-4 w-full">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="relative flex flex-col space-y-8 w-full"
					>
						<FormField
							control={form.control}
							name="rejection_reason"
							render={({ field }) => (
								<FormItem className="text-start">
									<FormControl>
										<Textarea
											className="px-6 pt-5 border border-border rounded-xl h-48 font-semibold placeholder:text-secondary resize-none"
											placeholder="describe the reason for the user rejection here..."
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							disabled={fetching}
							className="bg-rejectbuttonbg py-6 border border-rejectbuttonborder w-fit font-semibold text-rejectbuttontext tracking-wider self-end"
						>
							Remove user
						</Button>
					</form>
				</Form>
			</div>
		</DialogContent>
	);
};
